/**
 * 权限核心服务
 * @description 简化重构的权限检查服务，替代原有复杂的PermissionService
 * @author Claude Code Assistant
 * @date 2025-08-10
 * @version 2.0.0
 */

import { PERMISSION_GROUPS, PERMISSIONS, type PermissionString } from '@/constants'
import { UserRole } from '@/types/auth'
import { permissionCache } from './PermissionCache'

/**
 * 权限检查结果
 */
export interface PermissionResult {
  /** 是否允许访问 */
  allowed: boolean
  /** 结果原因 */
  reason: 'authorized' | 'not_authenticated' | 'insufficient_permission' | 'no_restriction'
  /** 详细信息 */
  details?: string
  /** 权限引导信息 */
  guidance?: {
    action: string
    path: string
    estimatedTime?: string
  }
}

/**
 * 用户信息接口
 */
export interface UserInfo {
  /** 用户ID */
  id?: string
  /** 用户角色 */
  role: UserRole
  /** 是否已登录 */
  isLoggedIn: boolean
  /** 自定义权限列表 */
  customPermissions?: string[]
}

/**
 * 页面配置接口
 */
export interface PageConfig {
  /** 是否需要认证 */
  requiresAuth?: boolean
  /** 需要的权限列表 */
  permissions?: string[]
  /** 权限不足时的回退操作 */
  fallbackAction?: string
}

/**
 * 简化的权限映射策略
 * 使用基础权限组合模式，减少重复定义
 */
const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  [UserRole.GUEST]: [],

  [UserRole.USER]: [
    ...PERMISSION_GROUPS.COMMON,
    ...PERMISSION_GROUPS.USER_BASE,
  ],

  [UserRole.DESIGNER]: [
    ...PERMISSION_GROUPS.COMMON,
    ...PERMISSION_GROUPS.USER_BASE,
    ...PERMISSION_GROUPS.CERTIFIED,
    ...PERMISSION_GROUPS.DESIGNER_EXCLUSIVE,
  ],

  [UserRole.MANUFACTURER]: [
    ...PERMISSION_GROUPS.COMMON,
    ...PERMISSION_GROUPS.USER_BASE,
    ...PERMISSION_GROUPS.CERTIFIED,
    ...PERMISSION_GROUPS.MANUFACTURER_EXCLUSIVE,
  ],

  [UserRole.ADMIN]: [
    PERMISSIONS.WILDCARD, // 管理员拥有所有权限
  ],

  [UserRole.SUPER_ADMIN]: [
    PERMISSIONS.WILDCARD, // 超级管理员拥有所有权限
  ],
}

/**
 * 权限核心服务类
 */
export class PermissionCore {
  /**
   * 检查单个权限
   */
  checkPermission(
    permission: PermissionString,
    userRole: UserRole,
    userId?: string,
    customPermissions?: string[],
  ): boolean {
    // 构建缓存key
    const cacheKey = this.buildCacheKey(permission, userRole, userId, customPermissions)

    // 尝试从缓存获取结果
    const cached = permissionCache.get(cacheKey)
    if (cached !== null) {
      return cached
    }

    // 执行权限检查
    const result = this.performPermissionCheck(permission, userRole, customPermissions)

    // 缓存结果
    permissionCache.set(cacheKey, result)

    return result
  }

  /**
   * 批量权限检查
   */
  checkMultiplePermissions(
    permissions: PermissionString[],
    userRole: UserRole,
    mode: 'any' | 'all' = 'any',
    userId?: string,
    customPermissions?: string[],
  ): boolean {
    const checks = permissions.map(permission =>
      this.checkPermission(permission, userRole, userId, customPermissions),
    )

    return mode === 'any' ? checks.some(Boolean) : checks.every(Boolean)
  }

  /**
   * 检查页面访问权限
   */
  checkPageAccess(
    pageConfig: PageConfig,
    userInfo: UserInfo,
  ): PermissionResult {
    // 认证检查
    if (pageConfig.requiresAuth && !userInfo.isLoggedIn) {
      return {
        allowed: false,
        reason: 'not_authenticated',
        details: '该页面需要登录后才能访问',
        guidance: {
          action: 'login',
          path: '/auth/login',
          estimatedTime: '1分钟',
        },
      }
    }

    // 权限检查
    if (pageConfig.permissions?.length) {
      const hasPermission = this.checkMultiplePermissions(
        pageConfig.permissions,
        userInfo.role,
        'any', // 只要有一个权限满足即可
        userInfo.id,
        userInfo.customPermissions,
      )

      if (!hasPermission) {
        return {
          allowed: false,
          reason: 'insufficient_permission',
          details: `缺少所需权限: ${pageConfig.permissions.join(', ')}`,
          guidance: this.generateGuidance(pageConfig.permissions, userInfo),
        }
      }
    }

    return {
      allowed: true,
      reason: 'authorized',
    }
  }

  /**
   * 获取用户所有权限
   */
  getUserPermissions(userRole: UserRole, customPermissions?: string[]): string[] {
    const rolePermissions = ROLE_PERMISSIONS[userRole] || []
    const validCustomPermissions = (customPermissions || []).filter(
      perm => perm && typeof perm === 'string',
    )
    const merged = new Set([...rolePermissions, ...validCustomPermissions])
    return Array.from(merged)
  }

  /**
   * 检查用户是否拥有指定角色
   */
  hasRole(userRole: UserRole, requiredRoles: UserRole[]): boolean {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true
    }
    return requiredRoles.includes(userRole)
  }

  /**
   * 获取角色升级路径
   */
  getUpgradeOptions(currentRole: UserRole, requiredPermissions: string[]): Array<{
    targetRole: UserRole
    providedPermissions: string[]
    estimatedTime: string
  }> {
    const upgradeOptions: Array<{
      targetRole: UserRole
      providedPermissions: string[]
      estimatedTime: string
    }> = []

    // 检查每个角色是否能提供所需权限
    for (const [role, permissions] of Object.entries(ROLE_PERMISSIONS)) {
      const targetRole = role as UserRole

      if (targetRole === currentRole || targetRole === UserRole.GUEST) {
        continue
      }

      const providedPermissions = requiredPermissions.filter(reqPerm =>
        permissions.includes(reqPerm)
        || permissions.includes(PERMISSIONS.WILDCARD)
        || this.checkWildcardMatch(reqPerm, permissions),
      )

      if (providedPermissions.length > 0) {
        upgradeOptions.push({
          targetRole,
          providedPermissions,
          estimatedTime: this.getUpgradeEstimatedTime(currentRole, targetRole),
        })
      }
    }

    return upgradeOptions.sort((a, b) =>
      this.getUpgradePriority(currentRole, a.targetRole)
      - this.getUpgradePriority(currentRole, b.targetRole),
    )
  }

  /**
   * 清理用户权限缓存
   */
  clearUserPermissionCache(userId: string): void {
    permissionCache.clearUserCache(userId)
  }

  /**
   * 清理角色权限缓存
   */
  clearRolePermissionCache(role: UserRole): void {
    permissionCache.clearRoleCache(role)
  }

  /**
   * 获取权限统计信息
   */
  getPermissionStats(): {
    totalPermissions: number
    rolePermissions: Record<UserRole, number>
    cacheStats: {
      totalChecks: number
      hits: number
      misses: number
      hitRate: number
      averageCheckTime: number
      cacheSize: number
      lastCleanup: number
    }
  } {
    const rolePermissions: Record<UserRole, number> = {} as Record<UserRole, number>

    for (const [role, permissions] of Object.entries(ROLE_PERMISSIONS)) {
      rolePermissions[role as UserRole] = permissions.length
    }

    // 计算所有唯一权限数量
    const allPermissions = new Set<string>()
    Object.values(ROLE_PERMISSIONS).forEach((permissions) => {
      permissions.forEach(permission => allPermissions.add(permission))
    })

    return {
      totalPermissions: allPermissions.size,
      rolePermissions,
      cacheStats: permissionCache.getStats(),
    }
  }

  /**
   * 预热权限缓存
   */
  async warmupCache(userRole: UserRole, userId?: string): Promise<void> {
    // 预计算常用权限
    const commonPermissions = [
      PERMISSIONS.USER.PROFILE.VIEW,
      PERMISSIONS.USER.TASKS.ACCEPT,
      PERMISSIONS.USER.FINANCE.WITHDRAW,
      PERMISSIONS.MODEL.VIEW,
      PERMISSIONS.MODEL.UPLOAD,
    ]

    const warmupData = commonPermissions.map(permission => ({
      key: this.buildCacheKey(permission, userRole, userId),
      value: this.performPermissionCheck(permission, userRole),
    }))

    await permissionCache.warmup(warmupData)
  }

  /**
   * 执行实际的权限检查逻辑
   */
  private performPermissionCheck(
    permission: PermissionString,
    userRole: UserRole,
    customPermissions?: string[],
  ): boolean {
    // 获取用户完整权限列表
    const userPermissions = this.getUserPermissions(userRole, customPermissions)

    // 管理员通配符权限
    if (userPermissions.includes(PERMISSIONS.WILDCARD)) {
      return true
    }

    // 直接权限匹配
    if (userPermissions.includes(permission)) {
      return true
    }

    // 通配符权限匹配
    return this.checkWildcardMatch(permission, userPermissions)
  }

  /**
   * 检查通配符权限匹配
   */
  /**
   * 检查通配符权限匹配
   */
  private checkWildcardMatch(permission: string, userPermissions: string[]): boolean {
    return userPermissions
      .filter(userPerm => userPerm && typeof userPerm === 'string') // 过滤无效值
      .some((userPerm) => {
        if (!userPerm.endsWith('*'))
          return false

        const prefix = userPerm.slice(0, -1) // 移除 '*'
        return permission.startsWith(prefix)
      })
  }

  /**
   * 构建缓存key
   */
  private buildCacheKey(
    permission: PermissionString,
    userRole: UserRole,
    userId?: string,
    customPermissions?: string[],
  ): string {
    // 🚀 性能优化：使用数组join代替字符串拼接
    const keyParts = [
      permission,
      userRole,
      userId || 'anon', // 缩短匿名用户标识
      customPermissions ? customPermissions.sort().join(',') : '', // 对权限列表排序确保一致性
    ]
    return keyParts.join(':')
  }

  /**
   * 生成权限引导
   */
  private generateGuidance(
    requiredPermissions: string[],
    userInfo: UserInfo,
  ): PermissionResult['guidance'] {
    const upgradeOptions = this.getUpgradeOptions(userInfo.role, requiredPermissions)

    if (upgradeOptions.length === 0) {
      return {
        action: 'contact_support',
        path: '/support/contact',
      }
    }

    const bestOption = upgradeOptions[0]

    // 特殊处理承接任务权限
    if (requiredPermissions.includes(PERMISSIONS.USER.TASKS.ACCEPT)) {
      return {
        action: 'upgrade_to_designer',
        path: '/user/designer-registration',
        estimatedTime: '5-15分钟',
      }
    }

    // 通用升级引导
    const upgradePaths: Record<UserRole, string> = {
      [UserRole.DESIGNER]: '/user/designer-registration',
      [UserRole.MANUFACTURER]: '/user/provider-registration',
      [UserRole.ADMIN]: '/support/contact',
      [UserRole.SUPER_ADMIN]: '/support/contact',
      [UserRole.USER]: '/user/settings',
      [UserRole.GUEST]: '/auth/register',
    }

    return {
      action: `upgrade_to_${bestOption.targetRole.toLowerCase()}`,
      path: upgradePaths[bestOption.targetRole] || '/user/settings',
      estimatedTime: bestOption.estimatedTime,
    }
  }

  /**
   * 获取升级预计时间
   */
  private getUpgradeEstimatedTime(currentRole: UserRole, targetRole: UserRole): string {
    const timeMap: Record<string, string> = {
      [`${UserRole.USER}-${UserRole.DESIGNER}`]: '5-15分钟',
      [`${UserRole.USER}-${UserRole.MANUFACTURER}`]: '10-20分钟',
      [`${UserRole.DESIGNER}-${UserRole.MANUFACTURER}`]: '15-30分钟',
      [`${UserRole.MANUFACTURER}-${UserRole.DESIGNER}`]: '10-25分钟',
    }

    return timeMap[`${currentRole}-${targetRole}`] || '联系客服'
  }

  /**
   * 获取升级优先级
   */
  private getUpgradePriority(currentRole: UserRole, targetRole: UserRole): number {
    // 从USER升级的优先级最高
    if (currentRole === UserRole.USER) {
      if (targetRole === UserRole.DESIGNER)
        return 1
      if (targetRole === UserRole.MANUFACTURER)
        return 2
    }

    // 其他升级路径
    if (targetRole === UserRole.ADMIN)
      return 10 // 管理员权限不常规推荐

    return 5 // 默认中等优先级
  }
}

/**
 * 创建权限核心服务实例
 */
export function createPermissionCore(): PermissionCore {
  return new PermissionCore()
}

/**
 * 全局权限核心服务实例（单例）
 */
export const permissionCore = createPermissionCore()

/**
 * 开发环境调试工具
 */
if (import.meta.env.DEV) {
  // 将权限服务暴露到全局，方便调试
  (window as any).__PERMISSION_CORE__ = {
    core: permissionCore,
    check: (permission: string, role: UserRole) =>
      permissionCore.checkPermission(permission as PermissionString, role),
    stats: () => permissionCore.getPermissionStats(),
    upgrade: (currentRole: UserRole, permissions: string[]) =>
      permissionCore.getUpgradeOptions(currentRole, permissions),
    warmup: (role: UserRole) => permissionCore.warmupCache(role),
  }

  // console.log('🛠️ [权限核心] 调试工具已注册到 window.__PERMISSION_CORE__')

  // 预热常用权限缓存
  permissionCore.warmupCache(UserRole.USER).then(() => {
    // console.log('🔥 [权限核心] USER角色权限缓存预热完成')
  })
}
